import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { db, auth } from './firebase';
import {
  collection, addDoc, query, orderBy, deleteDoc, doc, getDoc, getDocs, where
} from './firestore-compat';
import { useBusinessHoursPolling } from './hooks/useBusinessHoursPolling';

// ─── Parser XML genérico ──────────────────────────────────────────────────────
function parseXML(xmlText) {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, 'application/xml');

  const parseError = xmlDoc.querySelector('parsererror');
  if (parseError) throw new Error('XML inválido: ' + parseError.textContent.slice(0, 120));

  // Encontra o elemento que se repete mais (as "linhas" da tabela)
  const allElements = Array.from(xmlDoc.getElementsByTagName('*'));
  const tagCounts = {};
  allElements.forEach(el => {
    if (el.children.length === 0 || el.children.length > 0) {
      tagCounts[el.tagName] = (tagCounts[el.tagName] || 0) + 1;
    }
  });

  // O elemento que representa uma linha é o que aparece mais vezes com filhos
  const candidates = allElements
    .filter(el => el.children.length > 0 && el.parentElement)
    .map(el => el.tagName);

  const freqMap = {};
  candidates.forEach(t => { freqMap[t] = (freqMap[t] || 0) + 1; });

  const rowTag = Object.entries(freqMap)
    .filter(([, count]) => count > 1)
    .sort((a, b) => b[1] - a[1])[0]?.[0];

  if (!rowTag) throw new Error('Não foi possível identificar os registros no XML. Verifique o formato.');

  const rowElements = Array.from(xmlDoc.getElementsByTagName(rowTag));

  // Extrai campos de cada elemento — suporta tanto atributos quanto elementos filhos
  const rows = rowElements.map(el => {
    const obj = {};
    // Atributos
    Array.from(el.attributes).forEach(attr => {
      obj[attr.name] = attr.value.trim();
    });
    // Elementos filhos (text content)
    Array.from(el.children).forEach(child => {
      obj[child.tagName] = child.textContent.trim();
    });
    return obj;
  });

  if (rows.length === 0) throw new Error('Nenhum registro encontrado no XML.');

  const colunas = [...new Set(rows.flatMap(r => Object.keys(r)))];
  return { rows, colunas, rowTag, total: rows.length };
}

// ─── helpers ────────────────────────────────────────────────────────────────

/**
 * Converte hh:mm:ss em segundos (aceita string "00:02:34" ou número)
 */
function hmsToSeconds(val) {
  if (!val) return 0;
  if (typeof val === 'number') return val;
  const parts = String(val).split(':');
  if (parts.length === 3) {
    return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
  }
  return 0;
}

/**
 * Lê a planilha e normaliza para array de objetos com chaves fixas.
 * O arquivo tem cabeçalho mesclado em 2 linhas:
 *   Linha 1: Data | Origem | Destino (mergeado 3 colunas) | Categoria | Duração (mergeado 3) | Status | Protocolo
 *   Linha 2: (vazio) | (vazio) | Fila | Ramal | Agente | | Total | Espera | Falada | |
 * Então lemos a partir da linha 3 (index 2).
 */
function parseTelefonia(workbook, tipoPlanilha) {
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

  const rows = [];
  
  // Encontra onde a tabela realmente começa (procura a palavra "Data")
  let startRow = 2;
  for (let i = 0; i < raw.length; i++) {
    if (raw[i] && String(raw[i][0]).trim() === 'Data') {
      startRow = i + 2; // Pula a linha "Data" e a linha de subcabeçalho (Fila, Ramal...)
      break;
    }
  }

  for (let i = startRow; i < raw.length; i++) {
    const r = raw[i];
    // ignora linha vazia
    if (!r || !r[0]) continue;

    // Data pode vir como serial do Excel ou string
    let dataStr = r[0];
    if (typeof dataStr === 'number') {
      // serial date do Excel
      const d = XLSX.SSF.parse_date_code(dataStr);
      dataStr = `${String(d.d).padStart(2,'0')}/${String(d.m).padStart(2,'0')}/${d.y} ${String(d.H).padStart(2,'0')}:${String(d.M).padStart(2,'0')}:${String(d.S).padStart(2,'0')}`;
    }

    let rowData;
    if (tipoPlanilha === 'efetuadas') {
      // Outbound: Data | Origem Ramal | Agente | Destino | Categoria | Total | Falada | Status | Protocolo
      rowData = {
        data:           String(dataStr || '').trim(),
        origem:         String(r[1] || '').trim(),
        fila:           String(r[3] || '').trim(), // Destino salva como fila para gráficos
        ramal:          String(r[1] || '').trim(),
        agente:         String(r[2] || '').trim(),
        categoria:      String(r[4] || '').trim(),
        duracaoTotal:   hmsToSeconds(r[5]),
        duracaoEspera:  Math.max(0, hmsToSeconds(r[5]) - hmsToSeconds(r[6])), // Total - Falada
        duracaoFalada:  hmsToSeconds(r[6]),
        status:         String(r[7] || '').trim(),
        protocolo:      String(r[8] || '').trim(),
      };
    } else {
      // Inbound: Data | Origem | Fila | Ramal | Agente | Categoria | Total | Espera | Falada | Status | Protocolo
      rowData = {
        data:           String(dataStr || '').trim(),
        origem:         String(r[1] || '').trim(),
        fila:           String(r[2] || '').trim(),
        ramal:          String(r[3] || '').trim(),
        agente:         String(r[4] || '').trim(),
        categoria:      String(r[5] || '').trim(),
        duracaoTotal:   hmsToSeconds(r[6]),
        duracaoEspera:  hmsToSeconds(r[7]),
        duracaoFalada:  hmsToSeconds(r[8]),
        status:         String(r[9] || '').trim(),
        protocolo:      String(r[10] || '').trim(),
      };
    }
    rows.push(rowData);
  }
  return rows;
}

// ─── component ───────────────────────────────────────────────────────────────

export default function UploadPlanilha() {
  const [isDragging, setIsDragging]   = useState(false);
  const [file, setFile]               = useState(null);
  const [preview, setPreview]         = useState([]);
  const [parsedRows, setParsedRows]   = useState([]);
  const [colunas, setColunas]         = useState([]);
  const [xmlInfo, setXmlInfo]         = useState(null); // { rowTag, total }
  const [uploading, setUploading]     = useState(false);
  const [tipoPlanilha, setTipoPlanilha] = useState('recebidas');
  const [history, setHistory]         = useState([]);
  const [userName, setUserName]       = useState('');
  const [isAdmin, setIsAdmin]         = useState(false);
  const [deleting, setDeleting]       = useState(null);
  const [successMsg, setSuccessMsg]   = useState('');
  const [errorMsg, setErrorMsg]       = useState('');
  const fileInputRef = useRef();

  const currentUser = auth.currentUser;

  // Busca nome/role do usuário logado
  useEffect(() => {
    if (!currentUser) return;
    const fetch = async () => {
      const snap = await getDocs(query(collection(db, 'users'), where('email', '==', currentUser.email)));
      if (!snap.empty) {
        const d = snap.docs[0].data();
        setUserName(d.name || currentUser.email);
        const roles = Array.isArray(d.roles) ? d.roles : (d.role ? [d.role] : []);
        setIsAdmin(roles.includes('admin') || roles.includes('administrador'));
      }
    };
    fetch();
  }, [currentUser]);

  // Histórico de uploads — atualiza sozinho de hora em hora em horário comercial;
  // após um upload/exclusão feito por mim, força atualização na hora (ver
  // handleUpload/handleDelete abaixo).
  const carregarHistory = useCallback(async () => {
    const q = query(collection(db, 'dashboard_uploads'), orderBy('timestamp', 'desc'));
    const snap = await getDocs(q);
    setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, []);
  const historyPolling = useBusinessHoursPolling(carregarHistory);

  // Processa arquivo escolhido — suporta XML e XLSX/XLS
  const processFile = useCallback((f, tipo) => {
    if (!f) return;
    const isXML  = f.name.match(/\.xml$/i);
    const isXLSX = f.name.match(/\.(xlsx|xls)$/i);
    if (!isXML && !isXLSX) {
      setErrorMsg('Formatos aceitos: .xml, .xlsx, .xls');
      return;
    }
    setErrorMsg('');
    setFile(f);
    setXmlInfo(null);
    setColunas([]);

    const reader = new FileReader();

    if (isXML) {
      reader.onload = (e) => {
        try {
          const { rows, colunas: cols, rowTag, total } = parseXML(e.target.result);
          setParsedRows(rows);
          setColunas(cols);
          setPreview(rows.slice(0, 5));
          setXmlInfo({ rowTag, total });
        } catch (err) {
          setErrorMsg('Erro ao ler o XML: ' + err.message);
          setParsedRows([]); setColunas([]); setPreview([]);
          console.error(err);
        }
      };
      reader.readAsText(f, 'UTF-8');
    } else {
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: 'array', cellDates: false });
          const rows = parseTelefonia(wb, tipo);
          setParsedRows(rows);
          setColunas(Object.keys(rows[0] || {}));
          setPreview(rows.slice(0, 5));
        } catch (err) {
          setErrorMsg('Erro ao ler o arquivo. Verifique se é uma planilha válida.');
          console.error(err);
        }
      };
      reader.readAsArrayBuffer(f);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    processFile(f, tipoPlanilha);
  }, [processFile, tipoPlanilha]);

  const handleFileChange = (e) => processFile(e.target.files[0], tipoPlanilha);

  // Reprocessa se o usuário mudar o tipo após escolher o arquivo
  useEffect(() => {
    if (file) {
      processFile(file, tipoPlanilha);
    }
  }, [tipoPlanilha]);

  const handleUpload = async () => {
    if (!parsedRows.length) return;
    setUploading(true);
    setErrorMsg('');
    try {
      const safeRows = JSON.parse(JSON.stringify(parsedRows));
      const isXML = file?.name.match(/\.xml$/i);

      await addDoc(collection(db, 'dashboard_uploads'), {
        timestamp:    new Date().toISOString(),
        tipo:         tipoPlanilha,
        uploadedBy:   userName,
        uploadedByUid: currentUser?.uid || 'unknown',
        fileName:     file.name,
        totalRows:    safeRows.length,
        colunas:      colunas,
        formato:      isXML ? 'xml' : 'xlsx',
        xmlRowTag:    xmlInfo?.rowTag || null,
        rows:         safeRows,
      });
      setSuccessMsg(`✅ ${safeRows.length} registros salvos!`);
      setFile(null); setParsedRows([]); setPreview([]);
      setColunas([]); setXmlInfo(null);
      setTimeout(() => setSuccessMsg(''), 5000);
      historyPolling.refresh();
    } catch (err) {
      setErrorMsg(`Erro ao salvar: ${err.message}`);
      console.error(err);
    }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja excluir este upload e todos os seus dados?')) return;
    setDeleting(id);
    await deleteDoc(doc(db, 'dashboard_uploads', id));
    setDeleting(null);
    historyPolling.refresh();
  };

  const formatTs = (ts) => {
    if (!ts) return '--';
    const d = new Date(ts);
    return d.toLocaleString('pt-BR');
  };

  // ── estilos ──────────────────────────────────────────────────────────────

  const s = {
    page: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
      padding: '32px 24px',
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      color: '#e2e8f0',
    },
    header: {
      display: 'flex', alignItems: 'center', gap: '14px',
      marginBottom: '32px',
    },
    headerIcon: {
      width: 52, height: 52,
      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
      borderRadius: '14px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '1.6rem', boxShadow: '0 4px 20px rgba(99,102,241,0.4)',
    },
    title: { margin: 0, fontSize: '1.7rem', fontWeight: 800, color: '#f1f5f9' },
    subtitle: { margin: '4px 0 0', fontSize: '0.9rem', color: '#94a3b8' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', maxWidth: 1200 },
    card: {
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '16px', padding: '24px',
      backdropFilter: 'blur(10px)',
    },
    cardTitle: {
      fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: '#6366f1', marginBottom: '16px',
    },
    dropzone: (dragging) => ({
      border: `2px dashed ${dragging ? '#6366f1' : 'rgba(255,255,255,0.15)'}`,
      borderRadius: '12px',
      padding: '40px 24px',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s',
      background: dragging ? 'rgba(99,102,241,0.08)' : 'transparent',
    }),
    btn: (color, disabled) => ({
      padding: '12px 24px',
      borderRadius: '10px',
      border: 'none',
      fontWeight: 700,
      fontSize: '0.95rem',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      background: color === 'indigo'
        ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
        : color === 'red' ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.08)',
      color: color === 'red' ? '#f87171' : '#fff',
      transition: 'all 0.2s',
      width: color === 'indigo' ? '100%' : 'auto',
      marginTop: color === 'indigo' ? '16px' : 0,
    }),
    alert: (type) => ({
      padding: '12px 16px',
      borderRadius: '10px',
      marginBottom: '16px',
      fontSize: '0.9rem',
      fontWeight: 600,
      background: type === 'success' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
      border: `1px solid ${type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
      color: type === 'success' ? '#4ade80' : '#f87171',
    }),
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' },
    th: {
      padding: '8px 10px', textAlign: 'left',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      color: '#94a3b8', fontWeight: 600, fontSize: '0.72rem',
    },
    td: {
      padding: '8px 10px',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
      color: '#cbd5e1',
    },
    badge: (status) => ({
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '20px',
      fontSize: '0.72rem',
      fontWeight: 700,
      background: status === 'Atendida' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
      color: status === 'Atendida' ? '#4ade80' : '#f87171',
    }),
    histItem: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '14px 16px',
      borderRadius: '10px',
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.06)',
      marginBottom: '10px',
      gap: '12px',
    },
  };

  return (
    <div style={s.page}>

      {/* ── cabeçalho ── */}
      <div style={s.header}>
        <div style={s.headerIcon}>📤</div>
        <div>
          <h1 style={s.title}>Upload de Planilha</h1>
          <p style={s.subtitle}>Envie o relatório diário de telefonia para alimentar o dashboard</p>
        </div>
      </div>

      {successMsg && <div style={s.alert('success')}>{successMsg}</div>}
      {errorMsg   && <div style={s.alert('error')}>{errorMsg}</div>}

      <div style={{ ...s.grid, gridTemplateColumns: file ? '1fr 1.5fr' : '1fr' }}>

        {/* ── coluna esquerda: upload ── */}
        <div>
          <div style={s.card}>
            <div style={s.cardTitle}>📁 Selecionar Arquivo</div>

            {/* Tipo de Relatório */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', color: '#94a3b8', marginBottom: '8px', fontWeight: 600 }}>Tipo de Relatório</label>
              <select 
                value={tipoPlanilha}
                onChange={e => setTipoPlanilha(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '10px',
                  color: '#e2e8f0',
                  fontSize: '0.9rem',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="recebidas" style={{ background: '#1e293b' }}>📥 Chamadas Recebidas (Inbound)</option>
                <option value="efetuadas" style={{ background: '#1e293b' }}>📤 Chamadas Efetuadas (Outbound)</option>
              </select>
            </div>

            {/* Dropzone */}
            <div
              style={s.dropzone(isDragging)}
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current.click()}
            >
              <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📊</div>
              <div style={{ fontWeight: 700, color: '#e2e8f0', marginBottom: '6px' }}>
                {file ? file.name : 'Arraste o arquivo aqui ou clique para selecionar'}
              </div>
              <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
                Formatos aceitos: .xml, .xlsx, .xls
              </div>
              {file && parsedRows.length > 0 && (
                <div style={{ marginTop: '12px', fontSize: '0.82rem', color: '#6366f1', fontWeight: 600 }}>
                  {parsedRows.length} registros detectados ✔
                  {xmlInfo && <span style={{ color: '#10b981', marginLeft: 8 }}>· elemento: &lt;{xmlInfo.rowTag}&gt; · {colunas.length} campos</span>}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".xml,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>

            <button
              style={s.btn('indigo', !parsedRows.length || uploading)}
              disabled={!parsedRows.length || uploading}
              onClick={handleUpload}
            >
              {uploading ? '⏳ Enviando...' : `📤 Salvar ${parsedRows.length ? parsedRows.length + ' registros' : ''}`}
            </button>
          </div>

          {/* ── histórico ── */}
          <div style={{ ...s.card, marginTop: '20px' }}>
            <div style={s.cardTitle}>🕓 Histórico de Uploads</div>
            {history.length === 0 && (
              <div style={{ color: '#64748b', fontSize: '0.88rem' }}>Nenhum upload realizado ainda.</div>
            )}
            {history.map(h => (
              <div key={h.id} style={s.histItem}>
                <div>
                  <div style={{ fontWeight: 700, color: '#e2e8f0', fontSize: '0.88rem', marginBottom: '2px' }}>
                    📄 {h.fileName}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                    {formatTs(h.timestamp)} · {h.totalRows} registros · por {h.uploadedBy}
                  </div>
                </div>
                {isAdmin && (
                  <button
                    style={s.btn('red', deleting === h.id)}
                    disabled={deleting === h.id}
                    onClick={() => handleDelete(h.id)}
                  >
                    {deleting === h.id ? '⏳' : '🗑️'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── coluna direita: preview ── */}
        {file && preview.length > 0 && (
          <div style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <div style={s.cardTitle} >
                👁️ Prévia — primeiros {preview.length} de {parsedRows.length.toLocaleString('pt-BR')} registros
              </div>
              {xmlInfo && (
                <div style={{ fontSize: '0.75rem', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 8, padding: '4px 10px', color: '#4ade80' }}>
                  XML · &lt;{xmlInfo.rowTag}&gt; · {colunas.length} campos detectados
                </div>
              )}
            </div>

            {/* Campos detectados (XML) */}
            {xmlInfo && colunas.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Campos detectados no XML:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {colunas.map(c => (
                    <span key={c} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700, background: 'rgba(99,102,241,0.12)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.25)' }}>{c}</span>
                  ))}
                </div>
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    {(xmlInfo ? colunas : ['Data','Origem','Fila','Ramal','Agente','Categoria','Total(s)','Espera(s)','Falada(s)','Status','Protocolo']).map(h => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row, i) => (
                    <tr key={i}>
                      {(xmlInfo ? colunas : ['data','origem','fila','ramal','agente','categoria','duracaoTotal','duracaoEspera','duracaoFalada','status','protocolo']).map(col => (
                        <td key={col} style={s.td} title={row[col]}>
                          {xmlInfo
                            ? (row[col] || '—')
                            : col === 'status'
                              ? <span style={s.badge(row[col])}>{row[col]}</span>
                              : (row[col] || '—')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {parsedRows.length > 5 && (
              <div style={{ marginTop: '12px', fontSize: '0.8rem', color: '#64748b' }}>
                ...e mais {(parsedRows.length - 5).toLocaleString('pt-BR')} registros que serão salvos.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
